import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import ReactLinkify from "react-linkify";
import {
  CarouselProvider,
  Slider,
  Slide,
  ButtonBack,
  ButtonNext,
} from "pure-react-carousel";
// import AudioPlayer from "react-h5-audio-player";
import { TFunction, useTranslation } from "react-i18next";
import { DownloadButton, FallbackImage, ImageModal } from "../../../components";
import {
  Message as Model,
  MetaType,
  RequestStatus,
  ChatChannel,
  DeliveryStatus,
} from "../../../models";
import { useStore } from "../../../stores";
import locale from "../../../constants/locale";
import CatalogSlide from "./CatalogSlide";
import GenericSlide from "./GenericSlide";
import InstagramStory from "./InstagramStory";
import styles from "./Message.module.scss";
import HtmlRender from "./HtmlRender";
import VcfContact from "./VcfContact";
import ActionDropdown from "./ActionDropdown";
import MessageImage from "./MessageImage";
import LocationCard from "./LocationCard";
import OrderCard from "./OrderCard";
import VoiceCall from "./VoiceCall";
import MessageFormatter from "./MessageFormatter";
import MessageReplies from "./MessageReplies";
import WaFlowSent from "./WaFlowSent";
import WaFlowCustomResponse from "./WaFlowCustomResponse";
import MessageReplyPreview from "./MessageReplyPreview";
import { SingleActivityLog } from "./GroupedActivityLogs";
import CommentReference from "./CommentReference";
import WhatsappCall from "./WhatsappCall";
import { useMessageFiles } from "../../../hooks/useMessageFiles";
import firebase from "../../../services/firebase";
import { storageObject } from "../../../services/fileUrls";

const storageBucket = (firebase.options as { storageBucket?: string }).storageBucket || "";

const getSlide = (m: any, t: TFunction<"translation", undefined>) => {
  const resolveSrc = m.resolveFileUrl && m.payload?.url
    && storageObject(m.payload.url, storageBucket) !== null
    ? () => m.resolveFileUrl(m.payload.url) : undefined;
  let media: JSX.Element;
  switch (m.type) {
    case "image":
      media = <MessageImage src={m.payload?.url || ""} meta={m.payload?.meta}
        resolveSrc={resolveSrc} />;
      break;
    case "audio":
      media = (
        <div className={m.invertColors ? "audio-inverted" : ""}>
          <audio controls src={m.payload?.url} />
          {/* <AudioPlayer
            src={m.payload?.url}
            showJumpControls={false}
            autoPlay={false}
          /> */}
          <DownloadButton
            className={m.invertColors ? "has-text-white-bis" : ""}
            src={m.payload?.url}
            name={t(locale.download)}
            resolveSrc={resolveSrc}
          />
        </div>
      );
      break;
    case "video":
      media = (
        <>
          <div className="image is-16by9 has-rounded-corners">
            <video className="has-ratio" src={m.payload?.url} controls />
          </div>
          {m.payload?.meta?.caption && <p>{m.payload.meta.caption}</p>}
        </>
      );
      break;
    case "file":
      return (
        <DownloadButton
          className={m.invertColors ? "has-text-white-bis" : ""}
          src={m.payload?.url}
          name={m.text || m.payload?.file_name}
          resolveSrc={resolveSrc}
        />
      );
    case "vcard":
      return <VcfContact src={m.payload?.url} invertColors={m.invertColors}
        resolveSrc={resolveSrc} />;
    case "story_mention":
      media = (
        <>
          <InstagramStory
            url={m.payload?.url}
            setImageToShow={m.setImageToShow}
          />
          <h6
            className="title is-6 mb-1 mt-1 has-text-centered"
            style={{ opacity: 0.35 }}
          >
            {t(locale.story)}
          </h6>
        </>
      );
      break;
    case "referral":
      media = (
        <>
          {!!m.payload?.url && (
            <div
              className="image is-square has-rounded-corners is-clickable"
              onClick={() =>
                m.setImageToShow && m.setImageToShow(m.payload?.url)
              }
            >
              <FallbackImage
                src={m.payload.url}
                alt="media"
                customFallback={<span>{t(locale.resourceNotFound)}</span>}
              />
            </div>
          )}
          <div className={clsx(styles.referral, "my-1", "p-2")}>
            {!!m.payload?.headline && (
              <h6 className="title is-6 mb-1 has-text-centered">
                {m.payload.headline}
              </h6>
            )}
            {!!m.payload?.body && <p className="mx-1">{m.payload.body}</p>}
            {!!m.payload?.source && (
              <div className="has-text-centered py-2 mx-1">
                <button className="button is-light is-small is-fullwidth">
                  <a href={m.payload.source} target="_blank" rel="noreferrer">
                    {t(locale.viewSource, { type: m.payload.type })}
                  </a>
                </button>
              </div>
            )}
          </div>
        </>
      );
      break;
    case "share":
      media = (
        <>
          {!!m.payload?.url && (
            <>
              <div
                className="image is-square has-rounded-corners is-clickable"
                onClick={() =>
                  m.setImageToShow && m.setImageToShow(m.payload?.url)
                }
              >
                <FallbackImage
                  src={m.payload.url}
                  alt="media"
                  customFallback={<span>{t(locale.resourceNotFound)}</span>}
                />
              </div>
              <h6
                className="title is-6 mb-1 mt-1 has-text-centered"
                style={{ opacity: 0.35 }}
              >
                Instagram {t(locale.share)}
              </h6>
            </>
          )}
        </>
      );
      break;
    case "location":
      media = <LocationCard payload={m.payload} />;
      break;
    case "comment_reference":
      media = <CommentReference payload={m.payload} />;
      break;
    case "order":
      media = <OrderCard payload={m.payload} />;
      break;
    case "voice_call":
      media = <VoiceCall payload={m.payload} />;
      break;
    case "whatsapp_call":
      media = <WhatsappCall payload={m.payload} />;
      break;
    case "wa_flow":
      media = <WaFlowSent payload={m.payload} />;
      break;
    case "wa_flow_custom_response":
      media = <WaFlowCustomResponse payload={m.payload} />;
      break;
    case "wa_flow_response":
      media = (
        <div>
          <p
            style={{
              fontSize: 14,
            }}
            className={clsx("has-text-weight-bold", {
              "has-text-danger": !m.payload?.completed,
            })}
          >
            {m.payload?.completed ? "Flow completed" : "Flow failed"}
          </p>
        </div>
      );
      break;
    default:
      media = <div />;
      break;
  }

  if (m.text) {
    return (
      <>
        {media}
        <ReactLinkify
          componentDecorator={(decoratedHref, decoratedText, key) => (
            <a
              href={decoratedHref}
              key={key}
              target="_blank"
              rel="noopener noreferrer"
            >
              {decoratedText}
            </a>
          )}
        >
          {m.text}
        </ReactLinkify>
      </>
    );
  }

  return media;
};

const renderCall = (
  t: TFunction<"translation", undefined>,
  data: any,
  isFromUser: boolean,
  accept: () => void,
  reject: () => void
) => {
  const callStatusMap = {
    sent: `${t(locale.calling)}…`,
    rejected: t(locale.rejected),
    timed_out: t(locale.timedOut),
    none: "",
    canceled: t(locale.canceled),
    accepted: `${t(locale.ongoing)}…`,
    completed: t(locale.callEnded),
    agent_engaged: "",
  };

  return (
    <div className="is-flex">
      <div className="avatar">
        <span
          className={clsx("icon has-text-primary", {
            "has-text-danger": ["rejected", "timed_out", "canceled"].includes(
              data.status
            ),
          })}
        >
          <i className="fas fa-video"></i>
        </span>
      </div>
      <div className="is-flex-grow-1 is-flex is-flex-direction-column is-justify-content-center ml-4">
        <span>{callStatusMap[data.status as RequestStatus]}</span>
        {data.status === "completed" && (
          <span>
            {DateTime.fromISO(data.complete_time)
              .diff(DateTime.fromISO(data.response_time))
              .toFormat("mm:ss")}
          </span>
        )}
        {data.status === "sent" && isFromUser && (
          <div className="is-flex mt-2">
            <button
              type="button"
              className="button is-success mr-2"
              onClick={accept}
            >
              {t(locale.accept)}
            </button>
            <button type="button" className="button is-danger" onClick={reject}>
              {t(locale.reject)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// const LIVE_CHAT_EVENT_MESSAGE: Record<number, string> = {
//   [MetaType.LiveChatConnected]: locale.liveChatConnected,
//   [MetaType.LiveChatDisconnected]: locale.liveChatDisconnected,
// };

type Props = {
  onlyText?: boolean;
  message: Model;
  noReplies?: boolean;
  /** Isolated Agent previews: presentation only, never inbox actions. */
  preview?: { side: "left" | "right"; name: string; illustration?: boolean };
};

const Message = ({
  onlyText,
  noReplies,
  preview,
  message: {
    id,
    mId,
    text,
    reaction,
    senderId,
    createdAt,
    isFromUser,
    deliveryStatus,
    errorCode,
    errorMessage,
    attachment: sourceAttachment,
    attachments: sourceAttachments,
    firstName,
    lastName,
    isDeleted,
    confidential,
    metaType,
    meta,
    repliedOf,
  },
}: Props) => {
  const isRight = preview ? preview.side === "right" : !isFromUser;
  const { t } = useTranslation();
  const { users } = useStore("users");
  const { selectedChat, respondToVideo } = useStore("chats");
  const { selectedBot } = useStore("bots");
  const files = useMessageFiles({ attachment: sourceAttachment, attachments: sourceAttachments }, {
    botId: selectedBot?.id || 0,
    key: selectedBot?.key || "",
    password: selectedBot?.password || "",
    messageId: id,
    bucket: storageBucket,
  }, !preview && !onlyText && !isDeleted && id > 0);
  const { attachment, attachments } = files.data;
  const [imageToShow, setImageToShow] = useState("");
  const showImage = (url: string) => { void files.resolveUrl(url).then(setImageToShow).catch(() => {}); };
  const date = DateTime.fromJSDate(createdAt);
  const time = date.hasSame(DateTime.utc(), "day")
    ? date.toFormat("t")
    : date.toLocaleString({
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });

  const user = useMemo(() => {
    if (isFromUser) {
      return {
        name: firstName
          ? `${firstName} ${lastName || ""}`
          : `${selectedChat?.firstName || ""} ${selectedChat?.lastName || ""}`,
        image: selectedChat?.image,
      };
    }

    return users?.find((f) => f.id === senderId);
  }, [
    firstName,
    isFromUser,
    lastName,
    selectedChat?.firstName,
    selectedChat?.image,
    selectedChat?.lastName,
    senderId,
    users,
  ]);

  let fallbackUrl;

  if (isFromUser) {
    fallbackUrl = "/assets/avatar.svg";
  } else if (user) {
    fallbackUrl = "/assets/avatar.svg";
  } else {
    fallbackUrl = "/assets/bot.svg";
  }

  const image = (
    <figure className="is-flex-shrink-0">
      <p className="image is-32x32 is-square">
        <FallbackImage
          className="is-rounded"
          src={(user as any)?.avatarUrl}
          fallback={fallbackUrl}
          alt={user?.name || "user"}
        />
      </p>
    </figure>
  );

  // const isLiveChatAgentEvent =
  //   !!metaType &&
  //   [MetaType.LiveChatConnected, MetaType.LiveChatDisconnected].includes(
  //     metaType
  //   );

  // if (!text && isLiveChatAgentEvent) {
  //   return (
  //     <div
  //       className="is-flex py-3 is-relative is-justify-content-center is-align-items-center"
  //       style={{ opacity: 0.65 }}
  //     >
  //       <span>
  //         {t(LIVE_CHAT_EVENT_MESSAGE[metaType!], { agent: user?.name })}
  //       </span>
  //       <small className="ml-3">{time}</small>
  //     </div>
  //   );
  // }

  if (!text && metaType === MetaType.Transfer && meta?.to_bot_name) {
    return (
      <div
        className="is-flex py-3 is-relative is-justify-content-center is-align-items-center"
        style={{ opacity: 0.65 }}
      >
        <span>{t(locale.botTransferred, { name: meta?.to_bot_name })}</span>
        <small className="ml-3">{time}</small>
      </div>
    );
  }

  if (!text && metaType === MetaType.ActivityLog) {
    if (selectedBot?.settings?.disableChatHistory) {
      return null;
    }
    return (
      <SingleActivityLog
        message={
          {
            id,
            mId,
            text,
            reaction,
            senderId,
            createdAt,
            isFromUser,
            deliveryStatus,
            errorCode,
            errorMessage,
            attachment,
            attachments,
            firstName,
            lastName,
            isDeleted,
            confidential,
            metaType,
            meta,
            repliedOf,
          } as any
        }
        time={time}
      />
    );
  }

  if (!text && !attachment && !attachments?.length && !isDeleted) {
    if (isFromUser) {
      text = "Empty Message";
      deliveryStatus = DeliveryStatus.Failed;
      errorCode = "0";
      errorMessage = "Message is empty";
    } else {
      return null;
    }
  }

  const acceptVideo = () => respondToVideo(id, "accepted");
  const rejectVideo = () => respondToVideo(id, "rejected");

  let content: React.ReactNode = <MessageFormatter text={text ?? ""} />;

  if (onlyText) {
    return <>{content}</>;
  }

  if (attachment) {
    const data = attachment.payload;

    switch (attachment.type) {
      case "template":
        if (data?.template_type === "generic") {
          content = (
            <CarouselProvider
              naturalSlideWidth={280}
              naturalSlideHeight={280}
              isIntrinsicHeight
              totalSlides={data.elements.length}
            >
              <Slider>
                {data.elements.map((m: any, i: number) => (
                  <Slide index={i}>
                    <GenericSlide {...m} />
                  </Slide>
                ))}
              </Slider>
              <div className="is-flex is-justify-content-space-between mt-2">
                <ButtonBack className="button is-primary">{"<"}</ButtonBack>
                <ButtonNext className="button is-primary">{">"}</ButtonNext>
              </div>
            </CarouselProvider>
          );
        } else if (data?.template_type === "button") {
          content = <GenericSlide title={data.text} buttons={data.buttons} />;
        }
        break;
      // AI-agent catalog media. Same shape on both branches: one product uses a
      // single card because Meta rejects a one-card carousel.
      case "catalog-carousel":
      case "catalog-card": {
        const cards: any[] = data?.cards ?? [];
        content = (
          <>
            {!!data?.body && <MessageFormatter text={data.body} />}
            {cards.length === 1 ? (
              <CatalogSlide card={cards[0]} buttonTitle={data?.button_title} />
            ) : (
              <CarouselProvider
                naturalSlideWidth={280}
                naturalSlideHeight={280}
                isIntrinsicHeight
                totalSlides={cards.length}
              >
                <Slider>
                  {cards.map((m: any, i: number) => (
                    <Slide index={i} key={m.token_id ?? i}>
                      <CatalogSlide card={m} buttonTitle={data?.button_title} />
                    </Slide>
                  ))}
                </Slider>
                <div className="is-flex is-justify-content-space-between mt-2">
                  <ButtonBack className="button is-primary">{"<"}</ButtonBack>
                  <ButtonNext className="button is-primary">{">"}</ButtonNext>
                </div>
              </CarouselProvider>
            )}
          </>
        );
        break;
      }
      case "video_call":
        content = renderCall(t, data, isFromUser, acceptVideo, rejectVideo);
        break;
      case "html":
        content = (
          <div className="is-overflow-y-auto">
            <HtmlRender html={data.html ?? text} />
          </div>
        );
        break;
      default:
        content = getSlide(
          {
            ...attachment,
            setImageToShow: showImage,
            resolveFileUrl: preview ? undefined : files.resolveUrl,
            text,
            invertColors: preview ? isRight : true,
          },
          t
        );
        break;
    }
  }

  // Historical passive messages can contain []; that is text, not a carousel.
  if (attachments?.length) {
    if (attachments.length === 1) {
      if (attachments[0].type === "video_call") {
        const data = attachments[0].payload;
        content = renderCall(t, data, isFromUser, acceptVideo, rejectVideo);
      } else {
        content = getSlide({ ...attachments[0], setImageToShow: showImage, resolveFileUrl: preview ? undefined : files.resolveUrl, text }, t);
      }
    } else {
      content = (
        <CarouselProvider
          naturalSlideWidth={280}
          naturalSlideHeight={280}
          totalSlides={attachments.length}
        >
          <Slider>
            {attachments.map((m, i) => (
              <Slide index={i}>
                {getSlide({ ...m, setImageToShow: showImage, resolveFileUrl: preview ? undefined : files.resolveUrl, text }, t)}
              </Slide>
            ))}
          </Slider>
          <div className="is-flex is-justify-content-space-between mt-2">
            <ButtonBack className="button is-primary">{"<"}</ButtonBack>
            <ButtonNext className="button is-primary">{">"}</ButtonNext>
          </div>
        </CarouselProvider>
      );
    }
  }

  if (isDeleted) {
    content = t(locale.messageDeleted);
  }

  const isMessageFromWaba = meta?.is_message_from_waba;
  const isInformationalNotification = errorCode === "000";

  const error =
    ((deliveryStatus === "failed" || deliveryStatus === "undelivered") &&
      !isInformationalNotification &&
      "(" + errorCode + ") " + errorMessage) ||
    "";

  const infoMessage =
    ((deliveryStatus === "failed" || deliveryStatus === "undelivered") &&
      isInformationalNotification &&
      errorMessage) ||
    "";

  return (
    <article
      id={`chat-message-${id}`}
      onErrorCapture={files.onError}
      onLoadedMetadataCapture={files.onLoadedMetadata}
      className={clsx("is-flex py-3 is-relative", {
        "is-justify-content-flex-end": isRight,
        "is-justify-content-flex-start": !isRight,
      })}
    >
      {!isRight && !preview?.illustration && image}
      <div
        className={clsx(
          {
            "ml-3": !isRight,
            "mr-3": isRight,
          },
          "is-flex is-flex-direction-column"
        )}
      >
        <div
          className={clsx(
            "is-relative is-flex is-flex-direction-column is-align-items-stretch",
            noReplies ? styles.containerWithReplies : styles.container,
            {
              "bubble-left": !isRight,
              "bubble-right": isRight,
              "has-background-danger": error && !isInformationalNotification,
              [styles.deletedMessage]: isDeleted,
              "has-text-white": isFromUser && error && !isInformationalNotification,
              "has-background-warning-dark": confidential,
              "has-background-success":
                isMessageFromWaba && !error && !confidential,
            }
          )}
        >
          {!preview && <ActionDropdown messageId={id} isFromUser={isFromUser} />}
          {preview ? <small className="mb-2 has-text-weight-semibold">{preview.name}</small> : <small
            className={clsx(
              "is-flex is-justify-content-space-between is-align-items-center",
              {
                "mb-2":
                  (!isFromUser &&
                    (user?.name || (!isFromUser ? selectedBot?.name : ""))) ||
                  ["delivered", "read"].includes(deliveryStatus as any),
              }
            )}
          >
            {!isFromUser && (
              <>
                {isMessageFromWaba ? (
                  <strong>
                    <span className="icon is-small mr-1">
                      <i className="fab fa-whatsapp"></i>
                    </span>
                    {meta?.source === "meta_business_agent"
                      ? "Meta Business Agent"
                      : "WhatsApp Business App"}
                  </strong>
                ) : (
                  <strong className="mr-2">
                    {user?.name || (!isFromUser ? selectedBot?.name : "")}
                  </strong>
                )}
              </>
            )}
            {isFromUser && selectedChat?.channel === ChatChannel.ChatRoom && (
              <strong className="mr-2">{senderId}</strong>
            )}
            {isFromUser && selectedChat?.channel === ChatChannel.FBComment && (
              <strong className="mr-2">{firstName}</strong>
            )}
            <span
              className="is-flex"
              style={{
                position: "absolute",
                right: 5,
                bottom: 0,
              }}
            >
              {deliveryStatus === "delivered" && (
                <div className={clsx("ml-2", styles.messageStatus)}>
                  <i className="fas fa-check"></i>
                </div>
              )}
              {deliveryStatus === "read" && (
                <div className={clsx("ml-2", styles.messageStatus)}>
                  <i className="fas fa-check-double"></i>
                </div>
              )}
            </span>
          </small>}
          {repliedOf && (
            <MessageReplyPreview
              repliedOf={repliedOf}
              isFromUser={isFromUser}
            />
          )}
          <span
            className={clsx(styles.font13, "has-text-weight-medium")}
            style={{ wordBreak: "break-word", whiteSpace: "pre-wrap" }}
          >
            {content}
            {files.loading && <small role="status">{t(locale.loading)}...</small>}
            {files.failed && <small role="alert">{files.error || t(locale.resourceNotFound)}</small>}
          </span>
          {!!error && (
            <small
              className={clsx(
                "is-danger is-flex is-align-items-center mt-2",
                styles.messageStatus
              )}
            >
              <span className="icon has-text-white">
                <i className="fas fa-exclamation-triangle"></i>
              </span>
              {error}
            </small>
          )}
          {!!infoMessage && (
            <small
              className={clsx(
                "is-flex is-align-items-center mt-2",
                styles.messageStatus
              )}
              style={{ opacity: 0.7 }}
            >
              <span className="icon">
                <i className="fas fa-info-circle"></i>
              </span>
              <span className="ml-1">{infoMessage}</span>
            </small>
          )}
          {reaction && (
            <span
              className={clsx(
                "has-background-white has-border has-rounded-corners has-shadow is-rounded px-1",
                styles.reaction
              )}
              style={{
                right: !isFromUser ? undefined : 2,
                left: !isFromUser ? 10 : undefined,
              }}
              dangerouslySetInnerHTML={{ __html: reaction }}
            />
          )}
        </div>
        {!preview?.illustration && <div
          className={clsx(styles.timeColor, {
            "has-text-right": isRight,
          })}
        >
          {time}
        </div>}
        {!preview && !noReplies && <MessageReplies mId={mId} />}
      </div>
      <div></div>
      {isRight && !preview?.illustration && image}
      {!!imageToShow && (
        <ImageModal
          src={files.currentUrl(imageToShow)}
          isOpen
          onClose={() => setImageToShow("")}
        />
      )}
    </article>
  );
};

export default observer(Message);
